import { useEffect, useRef } from "react";
import { FALLBACK_COVER_IMAGE, warningIcon } from "../../assets/icons";
import usePlaylistStore from "../../store/playlistStore";
import usePlaylistSelectionStore from "../../store/playlistSelectionStore";
import type { TrackObject } from "../../types/tracksType";
import { handleCreatePlaylist } from "../../features/playlist/playlistActions";

function getPlaylistCover(track: TrackObject | null): string {
  if (!track) return FALLBACK_COVER_IMAGE;
  if ("albumImage" in track) return track.albumImage;

  return track.album?.images[2]?.url || FALLBACK_COVER_IMAGE;
}

function getIsFallbackCoverImage(track: TrackObject | null): boolean {
  if (!track) return true;
  if ("albumImage" in track) {
    return !track.albumImage || track.albumImage === FALLBACK_COVER_IMAGE;
  }

  return false;
}

const CreatePlaylist = () => {
  const isCreateVisible = usePlaylistStore((state) => state.isCreateVisible);
  const errorMessage = usePlaylistStore((state) => state.errorMessage);
  const isShaking = usePlaylistStore((state) => state.isShaking);
  const { setIsShaking, hideCreatePlaylistModal } = usePlaylistStore.getState();
  const selectedTrack = usePlaylistSelectionStore((state) => state.selectedTrack);

  const playlistNameRef = useRef<HTMLInputElement>(null);
  const playlistCover = getPlaylistCover(selectedTrack);
  const isFallbackCoverImage = getIsFallbackCoverImage(selectedTrack);
  const SHAKE_DURATION_MS = 600;

  useEffect(() => {
    if (isCreateVisible && playlistNameRef.current) {
      playlistNameRef.current.focus();
      playlistNameRef.current.value = "";
    }
  }, [isCreateVisible]);

  useEffect(() => {
    if (!isShaking) return;

    const timer = setTimeout(() => {
      setIsShaking(false);
    }, SHAKE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [isShaking]);

  return (
    <div className="playlist-page__create-playlist-modal modal" style={{ visibility: isCreateVisible ? "visible" : "hidden" }}>
      <div className="playlist-page__create-playlist-modal-smoke modal-smoke">
        <div className="playlist-page__create-playlist-modal-content modal-content">
          <h2 className="playlist-page__create-playlist-modal-title modal-title">新しいプレイリスト</h2>
          <div className="playlist-page__create-playlist-modal-cover-img-wrapper modal-cover-img-wrapper">
            {isFallbackCoverImage ? (
              <img className="playlist-page__create-playlist-modal-initial-cover-img" src={FALLBACK_COVER_IMAGE} />
            ) : (
              <img className="playlist-page__create-playlist-modal-cover-img" src={playlistCover} />
            )}
          </div>

          <div className="playlist-page__create-playlist-modal-field modal-field">
            <label className="playlist-page__create-playlist-modal-label modal-label" htmlFor="title">
              タイトル
            </label>
            <input
              className="playlist-page__create-playlist-modal-input modal-input"
              id="title"
              ref={playlistNameRef}
              onKeyDown={(e) => e.key === "Enter" && handleCreatePlaylist(playlistNameRef.current?.value ?? "")}
            />

            {errorMessage && (
              <p className={`error-text ${isShaking ? "shake" : ""}`}>
                <img src={warningIcon} />
                {errorMessage}
              </p>
            )}
          </div>
          <div className="playlist-page__create-playlist-modal-actions modal-actions">
            <button
              className="playlist-page__create-playlist-modal-cancel modal-cancel-submit-button modal-cancel-button"
              onClick={hideCreatePlaylistModal}
            >
              キャンセル
            </button>
            <button
              className="playlist-page__create-playlist-modal-create modal-cancel-submit-button modal-submit-button"
              onClick={() => handleCreatePlaylist(playlistNameRef.current?.value ?? "")}
            >
              作成
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePlaylist;
